<template>
  <div class="tax-amount-fields">
    <b-form-group
      horizontal
      :label="$t('netAmount')"
      label-for="netAmount"
      label-class="text-md-right"
      :label-cols="4"
      breakpoint="md"
    >
      <b-form-input
        id="netAmount"
        :value="amounts.netAmount"
        :disabled="isReadOnly"
        type="number"
        step="0.01"
        :placeholder="$t('netAmount')"
        @input="changeAmounts({ netAmount: toAmount($event) })"
      />
    </b-form-group>

    <b-form-group
      horizontal
      :label="$t('taxAmount')"
      label-for="taxCalculationMode"
      label-class="text-md-right"
      :label-cols="4"
      breakpoint="md"
    >
      <SelectButton
        id="taxCalculationMode"
        v-model="taxCalculationMode"
        :disabled="isReadOnly"
        class="calculation-mode-select"
        :options="automaticManualOptions"
      >
        <template #option="props">
          {{ $t(props.option) }}
        </template>
      </SelectButton>

      <Dropdown
        v-if="isTaxCalculationAutomatic"
        :value="amounts.vatPercentage"
        :disabled="isReadOnly"
        class="mt-2"
        :options="vatPercentageOptions"
        :placeholder="$t('vatPercentageState')"
        optionLabel="label"
        optionValue="value"
        @change="changeAmounts({ vatPercentage: $event.value })"
      />

      <b-form-input
        v-if="!isTaxCalculationAutomatic"
        id="taxAmount"
        class="mt-2"
        :value="amounts.taxAmount"
        :disabled="isReadOnly"
        type="number"
        step="0.01"
        :placeholder="$t('taxAmount')"
        @input="changeAmounts({ taxAmount: toAmount($event) })"
      />
      <div v-else class="form-control-plaintext">
        {{ formatNumber(amounts.taxAmount, NUMBER_FORMAT.CURRENCY) }}
      </div>
    </b-form-group>

    <b-form-group
      horizontal
      :label="$t('grossAmount')"
      label-for="grossAmount"
      label-class="text-md-right"
      :label-cols="4"
      breakpoint="md"
    >
      <div id="grossAmount" class="form-control-plaintext">
        {{ formatNumber(amounts.grossAmount, NUMBER_FORMAT.CURRENCY) }}
      </div>
    </b-form-group>
  </div>
</template>

<script>
import SelectButton from 'primevue/selectbutton';
import Dropdown from 'primevue/dropdown';
import { formatNumber, NUMBER_FORMAT } from '@/helpers/numberFormat';
import { calculateTaxAmounts, DEFAULT_VAT_PERCENTAGE } from '@/helpers/taxAmounts';

const MODE_AUTOMATIC = 'AUTOMATIC';
const MODE_MANUAL = 'MANUAL';

/**
 * Net amount, VAT rate or manual tax, tax and gross amount of an invoice or offer. Emits
 * `update:amounts` with all four fields; `calculate` lets the form apply its own rules first.
 */
export default {
  name: 'TaxAmountFields',
  components: {
    SelectButton,
    Dropdown,
  },
  props: {
    /** netAmount, vatPercentage, taxAmount, grossAmount */
    amounts: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    /** (amounts) => ({ taxAmount, grossAmount, ...changed fields }) */
    calculate: {
      type: Function,
      default: calculateTaxAmounts,
    },
  },
  data() {
    return {
      NUMBER_FORMAT,
      automaticManualOptions: [MODE_AUTOMATIC, MODE_MANUAL],
      vatPercentageOptions: [
        { value: 19, label: '19 %' },
        { value: 7, label: '7 %' },
        { value: 0, label: '0 %' },
      ],
    };
  },
  computed: {
    taxCalculationMode: {
      get() {
        return this.amounts.vatPercentage != null ? MODE_AUTOMATIC : MODE_MANUAL;
      },
      set(mode) {
        const isAutomatic = mode === MODE_AUTOMATIC;
        const vatPercentage = isAutomatic
          ? this.amounts.vatPercentage ?? DEFAULT_VAT_PERCENTAGE
          : null;
        this.changeAmounts({ vatPercentage });
      },
    },

    isTaxCalculationAutomatic() {
      return this.taxCalculationMode === MODE_AUTOMATIC;
    },
  },
  methods: {
    formatNumber,

    /** An empty input counts as no amount, as `v-model.number` did. */
    toAmount(value) {
      const amount = parseFloat(value);
      return Number.isNaN(amount) ? null : amount;
    },

    changeAmounts(changedFields) {
      const { netAmount, vatPercentage, taxAmount, grossAmount } = this.amounts;
      const amounts = { netAmount, vatPercentage, taxAmount, grossAmount, ...changedFields };
      this.$emit('update:amounts', { ...amounts, ...this.calculate(amounts) });
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .calculation-mode-select.p-selectbutton .p-button {
  padding: 0.1rem 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
}
</style>
